import { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import {
  Dialog,
  Disclose,
  EmptyState,
  Headline,
  Loading,
  Refusal,
  Tooltip,
} from "@lopie/kernel-ui";

/**
 * What have they learned, and what have we told them?
 *
 * ## Two kinds of thing, one type, one field
 *
 * A **memory** is inferred by an agent: it cites its sources, can be
 * re-checked against them, goes stale. A **knowledge
 * entry** is asserted by a person: it cites nothing, cannot be re-derived,
 * does not go stale on its own, and is true because somebody is accountable
 * for it.
 *
 * One store, because retrieval has to return both — an agent asking what it
 * knows about a vendor needs the standing instruction *and* the derived
 * observation, ranked together, and two stores means a merge that becomes a
 * second place truth lives.
 *
 * Rendered differently, because **a person's assertion is not evidence and an
 * agent's inference is not authority.** Show them the same and a reader
 * cannot tell whether a claim came from forty payments or from something
 * somebody typed once in March. The badge is on the row, not in a block at
 * the foot of the page.
 *
 * ## Editing an agent's memory replaces it
 *
 * The kernel refuses `memory.update` on an agent-asserted row with
 * `inference_is_not_editable`, and it is right to: a derived memory cites its
 * sources, and editing the sentence leaves `derived_from` naming records that
 * no longer say what it says. Re-verification then compares a human's wording
 * against evidence it was never drawn from, and nothing errors.
 *
 * So this screen does not offer an edit that would be refused. It offers
 * **Replace**, says what that will do before the person commits, and writes a
 * new person-asserted entry naming the old one in `supersedes`. The original
 * stays exactly as the agent wrote it, which is what makes it evidence of
 * what the agent concluded rather than a draft somebody overwrote.
 *
 * A row is superseded when something names it — there is no flag to set and
 * so no flag that can disagree with the fact.
 *
 * ## Tags come from the caller, never from a list here
 *
 * `permitted_tags` is echoed by every collection read and is the caller's own
 * ceiling. Offering a literal list would let somebody write an entry more
 * visible than themselves, which is the one thing this form must not do.
 *
 * Today that list is empty for `Memory` and the control is hidden rather than
 * rendered blank: a memory carries no tags until LO-121 computes them from
 * `derived_from`, and phase 1 memories hold no customer data at all.
 */
export default function Knowledge({ get, post }) {
  const [rows, setRows] = useState(null);
  const [permitted, setPermitted] = useState([]);
  const [topics, setTopics] = useState(null);
  const [addressees, setAddressees] = useState([]);
  // What one agent will read next run, straight from the kernel. `null`
  // until somebody asks for an agent — this is a question, not a default.
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState("");
  const [task, setTask] = useState("");
  const [stale, setStale] = useState(null);
  const [query, setQuery] = useState("");
  const [failure, setFailure] = useState(null);
  const [busy, setBusy] = useState(false);
  const [writing, setWriting] = useState(null);

  const load = useCallback(async () => {
    setFailure(null);
    try {
      // `topics=1` because the grouping is the kernel's to do: the vector
      // is stripped from every response by `_public`, so this page has
      // nothing to cluster with and serving it vectors would put a second
      // encoding of the content in the payload.
      // `/apps` alongside, because an entry has to be *addressed* and the
      // addresses are app types that declare an agent. Typing one was the
      // old design and it could not be right: the address is matched
      // exactly, so a typo saved cleanly and was read by nobody.
      // `stale=1` because re-checking is the only thing here that can say
      // how much of this is still true. It is asked for rather than
      // automatic — one governed read per source per memory — and the
      // kernel caps it and says when it stopped.
      const [body, register] = await Promise.all([
        get("/memories", { limit: 200, topics: 1, stale: 1 }),
        get("/apps"),
      ]);
      setRows(body.items || []);
      setPermitted(body.permitted_tags || []);
      setTopics(body.topics || null);
      setStale(body.stale || null);
      setAddressees(
        (register.apps || [])
          .filter((a) => !a.unparseable && (a.agents || []).length)
          .map((a) => ({
            app_type: a.app_type,
            label: a.label || a.app_type,
            // The agent's own name, which is *not* the address. The
            // `ledger-review` app runs an agent called `receivables-review`,
            // and a note addressed to `receivables-review` is read by
            // nothing. Shown so the person picking can recognise the agent
            // they mean; the value sent is always the app type.
            agents: (a.agents || []).map((g) => g.name || g).filter(Boolean),
          }))
          .sort((a, b) => a.label.localeCompare(b.label)),
      );
    } catch (e) {
      setFailure(e);
    }
  }, [get]);

  useEffect(() => { load(); }, [load]);

  /**
   * The lines `agent` gets at the top of its next run.
   *
   * `?recall=` rather than anything assembled here. The kernel picks which
   * half comes first, how many of each, and what a line says, and
   * `agents/runner.py` calls the same parameter — so this is the prompt,
   * not a description of it. A second implementation in this file would
   * agree with the runner until the day it did not, and a preview is worth
   * exactly its agreement with the thing it previews.
   */
  const lookAhead = useCallback(async (agent, forTask) => {
    if (!agent) { setPreview(null); setPreviewing(""); return; }
    setPreviewing(agent);
    setPreview(null);
    try {
      // `like` is the job, not a filter. The runner sends its kickoff; this
      // screen sends whatever somebody types. Same parameter, same ordering
      // — which is what keeps "this is the prompt" true rather than nearly
      // true. Left empty it falls back to recency, and the panel says so,
      // because a preview ordered differently from the run is worse than no
      // preview.
      const ask = { recall: agent, limit: 5 };
      if (forTask && forTask.trim()) ask.like = forTask.trim();
      const body = await get("/memories", ask);
      setPreview(body.recall || null);
    } catch (e) {
      setFailure(e);
      setPreviewing("");
    }
  }, [get]);

  // Searching is a kind of focus, so it shares the "N of M / show
  // everything" line rather than being a second way to have fewer rows on
  // screen with a different way of saying so.
  const search = useCallback(async (text) => {
    const wanted = text.trim();
    if (!wanted) { setFocus(null); return; }
    try {
      const body = await get("/memories", { like: wanted, limit: 20 });
      setFocus({
        kind: "like",
        value: wanted,
        // `?like=` ranks best LAST, because its first caller was a prompt
        // and the thing nearest the instruction is the thing most attended
        // to. A person reading a list expects the opposite, so it is
        // reversed here — a presentation choice, made where presentation is.
        ids: (body.items || []).map((r) => r.id).reverse(),
        scanned: body.scanned,
      });
    } catch (e) {
      setFailure(e);
    }
  }, [get]);

  const model = useMemo(() => {
    const all = rows || [];
    // A row is superseded when another names it. Computed here for the same
    // reason the kernel computes it: a stored flag is a second write that
    // can fail on its own and then disagree with the fact.
    const replaced = new Set(
      all.map((r) => (r.fields || {}).supersedes).filter(Boolean),
    );
    const live = all.filter((r) => !replaced.has(r.id)
      && (r.fields || {}).active !== "false");
    // Who wrote it, and what it is about. Two different questions that the
    // flat list could not tell apart: the `receivables-review` agent writes
    // notes whose subject is `ledger-review`, because the agent's name and
    // the app it runs for are not the same thing. A screen that showed only
    // one of them could not show that at all.
    const writerOf = (r) => {
      const p = r.provenance || {};
      if ((r.fields || {}).asserted_by === "person") return "a person";
      return p.agent || p.app_type || "an agent";
    };

    const edges = new Map();
    const subjects = new Map();
    const writers = new Map();
    for (const r of live) {
      const subject = (r.fields || {}).subject || "unattributed";
      const writer = writerOf(r);
      const key = `${writer}\u0000${subject}`;
      edges.set(key, { writer, subject, count: (edges.get(key)?.count || 0) + 1 });
      subjects.set(subject, (subjects.get(subject) || 0) + 1);
      writers.set(writer, (writers.get(writer) || 0) + 1);
    }

    const byName = (a, b) => a[0].localeCompare(b[0]);
    return {
      live,
      replaced,
      retired: all.filter((r) => (r.fields || {}).active === "false").length,
      stated: live.filter((r) => (r.fields || {}).asserted_by === "person").length,
      derived: live.filter((r) => (r.fields || {}).asserted_by !== "person").length,
      writerOf,
      edges: [...edges.values()],
      subjects: [...subjects.entries()].sort(byName),
      writers: [...writers.entries()].sort(byName),
      // A subject more than one writer has written into. That is the whole
      // of "shared" and it is worth naming rather than leaving a reader to
      // count lines in a picture.
      // Addresses an agent will actually ask for. An entry filed under
      // anything else is stored, listed, and read by nothing — which is what
      // a free-text subject box produced, and what the picker prevents from
      // here on. The ones already in that state are named rather than left
      // looking like the others.
      shared: [...subjects.keys()].filter(
        (sub) => new Set([...edges.values()]
          .filter((e) => e.subject === sub).map((e) => e.writer)).size > 1),
    };
  }, [rows]);

  /**
   * The subjects something will actually ask for.
   *
   * An app type that declares an agent, plus the broadcast address. Anything
   * else in the `subject` column is a dead letter: the kernel matches the
   * field exactly, so an entry addressed to a word no runner passes to
   * `?subject=` is stored, listed, mapped, and never read.
   *
   * There are five such entries in this kernel — `receivables-review`, which
   * is an agent's *name* rather than the app type it runs under, and
   * `vector-demo`. Both were written through a free-text box captioned "an
   * agent name", which is the reason the box is gone.
   *
   * Empty while `/apps` is in flight, and nothing is marked until it answers:
   * a page that flashed "nobody reads this" over every entry for a moment
   * would be saying something false in the direction that matters.
   */
  const addressable = useMemo(() => new Set([
    BROADCAST, ...addressees.map((a) => a.app_type),
  ]), [addressees]);

  // What the list is showing. `null` is everything, which is the state the
  // screen opens in: a filter nobody chose should not be applied.
  const [focus, setFocus] = useState(null);

  const shown = useMemo(() => {
    if (!focus) return model.live;
    if (focus.kind === "subject") {
      return model.live.filter((r) => ((r.fields || {}).subject || "unattributed") === focus.value);
    }
    if (focus.kind === "topic") {
      const members = new Set(focus.members || []);
      return model.live.filter((r) => members.has(r.id));
    }
    if (focus.kind === "like") {
      // Kept in the kernel's order rather than the page's. The ranking is
      // the answer; re-sorting it here would quietly throw away what was
      // asked for.
      const by = new Map(model.live.map((r) => [r.id, r]));
      return (focus.ids || []).map((id) => by.get(id)).filter(Boolean);
    }
    return model.live.filter((r) => model.writerOf(r) === focus.value);
  }, [model, focus]);

  /**
   * One entry, or one per agent it was addressed to.
   *
   * ## Why a list and not a payload
   *
   * `subject` holds one address and is matched exactly, so telling three
   * agents something is three rows. The alternative was a repeated field, and
   * it would make every reader of a memory — recall, the map, re-checking —
   * handle a set where it handles a value, to save a row.
   *
   * Three rows is also the truer record: each one is a thing said to one
   * agent, and each can be replaced or retired on its own without silently
   * un-telling the other two.
   *
   * ## Sequential, and it stops at the first refusal
   *
   * Not for rate limiting — so that a partial write is *reported as one*. If
   * the second of three is refused, the first stands, and the failure names
   * which addressees were not written rather than leaving the person to
   * count rows on the screen behind the dialog.
   */
  async function write(writes) {
    setBusy(true);
    const done = [];
    try {
      for (const one of writes) {
        await post(one.path, one.body);
        done.push(one.addressee);
      }
      setWriting(null);
      await load();
    } catch (e) {
      const missed = writes
        .map((w) => w.addressee)
        .filter((a) => a && !done.includes(a));
      setFailure(missed.length && done.length
        ? Object.assign(e, {
          detail: `${done.join(", ")} written; ${missed.join(", ")} not. `
            + `${e.detail || e.message || ""}`,
        })
        : e);
      await load();
    } finally {
      setBusy(false);
    }
  }

  /**
   * Delete, which is not retire, and the screen has to say which is which.
   *
   * **Retire** says this stopped being true. The entry stays, readable by
   * id, and the fact that somebody decided it was wrong is itself worth
   * keeping — that is the right move for a conclusion the world overtook.
   *
   * **Delete** says this should never have existed. The field values go and
   * do not come back. What survives is a tombstone: the row, its id, its
   * tags and the audit entry naming who removed it, so reading it afterwards
   * answers 410 rather than 404 and "somebody removed this" stays a
   * different sentence from "this never existed" (LO-49).
   *
   * Confirmed rather than immediate, and the confirmation quotes the entry.
   * Not a nicety: this is the only control in this app with no undo, and a
   * row of small buttons where one of them is irreversible is a row that
   * gets misclicked.
   */
  async function forget(row) {
    const text = (row.fields || {}).content || "";
    const sure = window.confirm(
      "Delete this entry?\n\n"
      + `\u201c${text.slice(0, 160)}${text.length > 160 ? "\u2026" : ""}\u201d\n\n`
      + "The words go and cannot be recovered. What stays is that it "
      + "existed and who removed it. If it was true once and is not now, "
      + "Retire is the better move \u2014 that keeps the entry as the record of "
      + "a decision.");
    if (!sure) return;

    setBusy(true);
    try {
      await post("/actions/memory.delete", { id: row.id });
      await load();
    } catch (e) {
      setFailure(e);
    } finally {
      setBusy(false);
    }
  }

  async function retire(row) {
    // Retiring is an update setting `active: false`, not a delete verb.
    // There is no `memory.retire`: a delete-shaped verb for one type would
    // settle LO-49 for the whole kernel as a side effect of a feature
    // nobody reviewed for it, and a deleted memory takes its own audit
    // trail with it.
    //
    // On an agent's row the kernel refuses an update outright, so retiring
    // one is the same act as replacing it: say so rather than offering a
    // button that 409s.
    setBusy(true);
    try {
      await post("/actions/memory.update", {
        id: row.id, fields: { active: "false" },
      });
      await load();
    } catch (e) {
      setFailure(e);
    } finally {
      setBusy(false);
    }
  }

  if (failure) {
    return <Refusal failure={failure} actor="Agent OS" onRetry={load} />;
  }
  if (!rows) return <Loading label="Reading what they know" />;

  return (
    <Stack spacing={3}>
      <Headline
        figure={String(model.live.length)}
        caption="things known"
        basis={
          "What agents have worked out and what people have told them, in "
          + "one list because an agent asking what it knows needs both. "
          + `${model.stated} stated by a person, ${model.derived} inferred `
          + "by an agent. A replaced note is not counted and is still there."
        }
      />

      <KnowledgeMap
        writers={model.writers}
        subjects={model.subjects}
        edges={model.edges}
        shared={model.shared}
        addressable={addressable}
        focus={focus}
        onFocus={setFocus}
      />

      <Topics
        topics={topics}
        focus={focus}
        onFocus={setFocus}
      />

      <StillTrue stale={stale} />

      <NextRun
        addressees={addressees}
        agent={previewing}
        preview={preview}
        task={task}
        onTask={setTask}
        onAgent={lookAhead}
      />

      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", alignItems: "center" }}>
        <Button variant="contained" size="small"
                onClick={() => setWriting({ mode: "new" })}>
          Tell them something
        </Button>
        <Box component="form" sx={{ display: "flex", gap: 1 }}
             onSubmit={(e) => { e.preventDefault(); search(query); }}>
          <TextField
            size="small"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by meaning"
            aria-label="Search entries by meaning"
            sx={{ minWidth: "18rem" }}
          />
          <Button type="submit" size="small" disabled={!query.trim()}>
            Search
          </Button>
        </Box>
        {focus ? (
          <>
            <Typography variant="body2" color="text.secondary">
              {/* A search is a reordering, not a narrowing, and it must not
                  borrow the sentence that belongs to one. `?like=` ranks
                  every row the caller can see and returns the closest; with
                  fewer entries than the limit that is all of them, and
                  "12 of 12" claims a filter that did not happen. */}
              {focus.kind === "like"
                ? `Closest to \u201c${focus.value}\u201d`
                : focus.kind === "subject"
                  ? `To ${addressLabel(focus.value)}`
                  : focus.kind === "topic"
                    ? `On \u201c${focus.value}\u201d`
                    : `Written by ${focus.value}`}
              {focus.kind === "like"
                ? ` \u00b7 ${shown.length} of ${focus.scanned ?? model.live.length}, best first`
                : ` \u00b7 ${shown.length} of ${model.live.length}`}
            </Typography>
            <Button size="small"
                    onClick={() => { setFocus(null); setQuery(""); }}>
              Show everything
            </Button>
          </>
        ) : null}
      </Stack>

      {focus?.kind === "like" && shown.length >= model.live.length ? (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "62ch" }}>
          Ranked rather than filtered: every entry you can see is still here,
          closest first. Nothing was left out, so a poor match at the bottom
          is the worst match rather than a result.
        </Typography>
      ) : null}

      {model.live.length > 0 && shown.length === 0 ? (
        <EmptyState
          title="Nothing here yet"
          detail={
            focus.kind === "subject"
              ? `Nothing has been written to ${addressLabel(focus.value)}.`
              : focus.kind === "topic"
                ? "Nothing in this topic is still current."
                : focus.kind === "like"
                  ? `Nothing reads like \u201c${focus.value}\u201d. Searched by meaning rather than by word, so a term `
                    + `that appears in an entry can still rank below one `
                    + `where it does not.`
                  : `${focus.value} has not written anything.`
          }
        />
      ) : null}

      {model.live.length === 0 ? (
        <EmptyState
          title="Nothing written down yet"
          detail={
            "An agent files what a run cost it to operate, and a person can "
            + "add a standing instruction. Neither has happened here."
          }
        />
      ) : null}

      <Stack spacing={1.5}>
        {shown.map((row) => (
          <Entry
            key={row.id}
            row={row}
            busy={busy}
            get={get}
            addressable={addressable}
            onReplace={() => setWriting({ mode: "replace", row })}
            onEdit={() => setWriting({ mode: "edit", row })}
            onRetire={() => retire(row)}
            onForget={() => forget(row)}
          />
        ))}
      </Stack>

      {model.retired > 0 || model.replaced.size > 0 ? (
        <Disclose summary="What is not shown above">
          <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "62ch" }}>
            {model.replaced.size > 0
              ? `${model.replaced.size} replaced by a later entry. `
              : ""}
            {model.retired > 0 ? `${model.retired} retired. ` : ""}
            Neither is deleted. A replaced note is the evidence of what an
            agent concluded and a retired one is the record that somebody
            decided it was wrong — both are still readable by id, and a
            deleted row would take its own audit trail with it.
          </Typography>
        </Disclose>
      ) : null}

      {writing ? (
        <WriteDialog
          intent={writing}
          permitted={permitted}
          addressees={addressees}
          busy={busy}
          onClose={() => setWriting(null)}
          onWrite={write}
        />
      ) : null}
    </Stack>
  );
}

//: The address that means "every agent", including ones added later.
//:
//: `subject` is matched exactly by the kernel and by `recall`, so an entry
//: reaches the agent whose app type it names and no other. That makes "tell
//: one" and "tell these three" ordinary writes, and makes "tell all"
//: impossible as a list: the set is not closed, and three rows written today
//: miss the agent added next month.
//:
//: `*` is not a legal app type, so it cannot collide with one. The runner
//: resolves it by reading it as a second subject (`agents/runner.py`
//: `BROADCAST`) rather than the kernel treating a field value as a wildcard —
//: a store that did would return rows not carrying the value asked for.
const BROADCAST = "*";

//: How a subject reads on screen. The stored value is the address; `*` is
//: not a word anybody typed and should not be shown as though it were.
const addressLabel = (subject) => (
  subject === BROADCAST ? "every agent" : subject
);

//: One row of the map, in pixels. Fixed rather than measured so an edge can
//: be drawn from an index without waiting for layout: a `ResizeObserver`
//: here would buy nothing except a frame where the lines are in the wrong
//: place.
const ROW_H = 44;
const ROW_GAP = 8;
const STEP = ROW_H + ROW_GAP;
//: Wide enough for the longest address plus a badge beside it. At 210 the
//: two dead addresses rendered as `receiv…` and `vector…`, which loses the
//: half a reader needs: the badge says something is wrong and the name says
//: which one.
const COL_W = 270;
const GUTTER = 96;

const rowY = (i) => i * STEP + ROW_H / 2;

/**
 * Who writes what, and where two of them meet.
 *
 * ## Why a bipartite map and not a hairball
 *
 * There are two questions and the flat list could answer neither: *what does
 * this agent know*, and *who else knows about the same thing*. They are the
 * two sides of one relation — a writer, and what it wrote about — so the
 * shape the data has is two columns and lines between them. A force-directed
 * graph of the same edges would be the same information, arranged so that
 * neither question is answerable at a glance.
 *
 * The two are genuinely different. The `receivables-review` agent writes
 * notes whose subject is `ledger-review`, because the agent's name and the
 * app it runs for are not the same thing. On a list keyed by one of them that
 * is invisible.
 *
 * ## The picture is not the only way in
 *
 * `RecordGraph` says it and it applies here: *"an SVG is not
 * keyboard-navigable and colour is not a signal a colour-blind operator can
 * read, so everything in the picture is also in the list underneath, in
 * words."*
 *
 * So the nodes are real `<button>`s in ordinary flow, each carrying its own
 * name and count as text, and the lines are an `aria-hidden` layer behind
 * them with no pointer events. Tab reaches every node in order; the SVG is
 * decoration over a list that already works. Nothing is only in the drawing:
 * a shared subject is named in words under it, because counting lines in a
 * picture is not reading.
 *
 * ## What an edge means, exactly
 *
 * One writer wrote N memories whose subject was that node. It is not a claim
 * that the writer can *read* them — a memory carries the tags of what it was
 * derived from, so an agent can write one it will not see returned. The line
 * says who wrote, and the list says what is visible to you.
 */
function KnowledgeMap({ writers, subjects, edges, shared, addressable,
                       focus, onFocus }) {
  const theme = useTheme();
  if (writers.length === 0) return null;

  const height = Math.max(writers.length, subjects.length) * STEP;
  const width = COL_W * 2 + GUTTER;
  const writerAt = new Map(writers.map(([name], i) => [name, i]));
  const subjectAt = new Map(subjects.map(([name], i) => [name, i]));
  const busiest = Math.max(...edges.map((e) => e.count), 1);

  const dim = (e) => {
    if (!focus) return false;
    return focus.kind === "subject"
      ? e.subject !== focus.value
      : e.writer !== focus.value;
  };

  return (
    <Box>
      <Typography variant="overline" sx={{ display: "block", color: "text.primary" }}>
        Who wrote it, and who reads it
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "62ch", mb: 1.5 }}>
        Who wrote it on the left, who it was written to on the right. The
        right-hand side is an address, matched exactly, so an agent reads what
        names it and nothing else — and the two sides are not the same name:
        the Ledger Review app runs an agent called receivables-review, so it
        writes on the left and is addressed on the right under a different
        word. Choose either to narrow the list. What the entries are
        <em> about</em> is the grouping below, which is a different question.
      </Typography>

      <Box sx={{ overflowX: "auto", pb: 1 }}>
        <Box sx={{ position: "relative", width, minHeight: height }}>
          {/* Decoration. Every edge it draws is also a number on the node it
              ends at, and the nodes are buttons in the flow above it. */}
          <Box
            component="svg"
            aria-hidden="true"
            viewBox={`0 0 ${width} ${height}`}
            sx={{
              position: "absolute",
              inset: 0,
              width,
              height,
              pointerEvents: "none",
            }}
          >
            {edges.map((e) => {
              const y1 = rowY(writerAt.get(e.writer) ?? 0);
              const y2 = rowY(subjectAt.get(e.subject) ?? 0);
              const x1 = COL_W;
              const x2 = COL_W + GUTTER;
              const mid = x1 + GUTTER / 2;
              return (
                <path
                  key={`${e.writer}-${e.subject}`}
                  d={`M ${x1} ${y1} C ${mid} ${y1}, ${mid} ${y2}, ${x2} ${y2}`}
                  fill="none"
                  stroke={theme.palette.primary.main}
                  strokeWidth={1 + (e.count / busiest) * 3}
                  opacity={dim(e) ? 0.12 : 0.45}
                />
              );
            })}
          </Box>

          <Box sx={{ display: "flex", gap: `${GUTTER}px`, position: "relative" }}>
            <Stack spacing={`${ROW_GAP}px`} sx={{ width: COL_W }}>
              {writers.map(([name, count]) => (
                <MapNode
                  key={name}
                  label={name}
                  count={count}
                  kind="writer"
                  active={focus?.kind === "writer" && focus.value === name}
                  onClick={() => onFocus(
                    focus?.kind === "writer" && focus.value === name
                      ? null : { kind: "writer", value: name })}
                />
              ))}
            </Stack>
            <Stack spacing={`${ROW_GAP}px`} sx={{ width: COL_W }}>
              {subjects.map(([name, count]) => (
                <MapNode
                  key={name}
                  label={addressLabel(name)}
                  count={count}
                  kind="subject"
                  shared={shared.includes(name)}
                  // Only once `/apps` has answered. Marking everything as
                  // unread while the register is in flight would be a false
                  // claim in the direction that matters.
                  unread={addressable.size > 1 && !addressable.has(name)}
                  active={focus?.kind === "subject" && focus.value === name}
                  onClick={() => onFocus(
                    focus?.kind === "subject" && focus.value === name
                      ? null : { kind: "subject", value: name })}
                />
              ))}
            </Stack>
          </Box>
        </Box>
      </Box>

      {/* In words, because counting lines in a picture is not reading. */}
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "62ch", mt: 1 }}>
        {shared.length
          ? `${shared.map(addressLabel).join(", ")} ${shared.length === 1 ? "is" : "are"} written into by more than one of them.`
          : "Nothing here has been written into by more than one of them yet, so nothing is shared."}
      </Typography>

      {/* The map is of what you can see, and saying so is not a formality.
          A memory carries the tags of what it was derived from, so one drawn
          from a customer-tagged record is tagged too — and this app declares
          `Memory` with no tags, which means it reads the untagged ones only.
          There is such a memory in this kernel right now and it is not on
          this picture.

          A map that looked complete and was not is the failure `RecordGraph`
          names: *"a diagram missing a piece with no sign a piece is missing
          is worse than no diagram, because it is believed."* The difference
          here is that this screen cannot even count what it is missing — the
          kernel returns what you may read and says nothing about the rest —
          so the honest sentence is about the map rather than about a
          number. */}
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "62ch", mt: 0.5 }}>
        This is what you can see. A memory inherits the tags of the records it
        was drawn from, so one about something you cannot read is not on this
        map and is not counted above — and nothing here can tell you how many
        those are.
      </Typography>
    </Box>
  );
}

/**
 * One node: a button, because it filters the list and because a picture
 * nobody can tab to is a picture some readers do not have.
 */
function MapNode({ label, count, kind, shared, unread, active, onClick }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={active}
      sx={{
        height: ROW_H,
        px: 1.25,
        display: "flex",
        alignItems: "center",
        gap: 1,
        minWidth: 0,
        textAlign: "left",
        font: "inherit",
        cursor: "pointer",
        borderRadius: 1,
        border: 1,
        borderColor: active ? "primary.main" : "divider",
        // Opaque, so the lines behind pass under the node rather than
        // through the words on it.
        backgroundColor: "background.paper",
        color: "text.primary",
        "&:hover": { borderColor: "primary.main" },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      <Typography variant="body2" sx={{ flexGrow: 1, minWidth: 0,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        fontWeight: active ? 600 : 400 }}>
        {label}
      </Typography>
      {shared ? (
        <Tooltip title="More than one writer has written about this. That is the only kind of sharing there is here: an agent and a person, or two agents, with notes about the same thing.">
          <Chip size="small" variant="outlined" label="shared" />
        </Tooltip>
      ) : null}
      {unread ? (
        <Tooltip title="No agent asks for this address. A run recalls what names its app type, matched exactly, so these entries are stored and listed and read by nothing — usually because the address is an agent's own name rather than the app it runs under. Write them again to a name from the list.">
          {/* A word, not only a colour: the whole of what is wrong here is
              unreadable if the signal is a shade of grey. */}
          <Chip size="small" variant="outlined" color="warning"
                label="no reader" />
        </Tooltip>
      ) : null}
      <Typography variant="body2" color="text.secondary"
                  sx={{ fontVariantNumeric: "tabular-nums" }}>
        {count}
      </Typography>
    </Box>
  );
}

/**
 * How much of what the brain knows is still true.
 *
 * ## Why a count is worth having
 *
 * Re-checking existed and ran one record at a time, on a click — so the only
 * way to find out a conclusion had gone stale was to already suspect it. A
 * brain that cannot say how much of itself still holds has to be trusted
 * wholesale or not at all, and neither is a position anybody should be in
 * about a thing their agents are reading from.
 *
 * ## What it does not prove
 *
 * `gone` means **this caller cannot read the source now**. That is the honest
 * answer from where you stand and it is not a claim the record was deleted —
 * a source hidden by a tag you do not carry looks the same from here. The
 * sentence saying so comes from the kernel with the answer rather than being
 * written on this page, where it could drift from what was actually checked.
 *
 * A person's entry is never checked. It cites nothing and is true because
 * somebody is accountable for it, which is not a thing evidence settles.
 */
function StillTrue({ stale }) {
  if (!stale) return null;
  const wrong = stale.needs_a_look || [];
  const beyond = stale.out_of_reach || [];
  if (!stale.checked && !beyond.length) return null;

  return (
    <Box>
      <Typography variant="overline" sx={{ display: "block", color: "text.primary" }}>
        Is it still true
      </Typography>

      {stale.checked === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "62ch" }}>
          Nothing here could be re-checked from this app. Every conclusion
          that cites evidence rests on a type this app does not declare, so
          the check is out of reach rather than failing — which is a
          manifest to widen, not a memory to go and look at.
        </Typography>
      ) : wrong.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "62ch" }}>
          All {stale.checked} of the conclusions this app can re-check still
          rest on records saying what they said.
        </Typography>
      ) : (
        <Alert severity="warning" variant="outlined" sx={{ maxWidth: "62ch" }}>
          <AlertTitle>
            {wrong.length} of {stale.checked} need a look
          </AlertTitle>
          {/* Named, not just counted — "three moved" with nothing saying
              which is a worry with nowhere to take it. */}
          <Stack spacing={0.5} sx={{ mt: 0.5 }}>
            {wrong.map((w, i) => (
              <Typography key={w.id || i} variant="body2">
                <Box component="span"
                     sx={{ fontFamily: "monospace", mr: 0.75 }}>
                  {w.verdict}
                </Box>
                {w.content}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary"
                  sx={{ display: "block", mt: 1, maxWidth: "62ch" }}>
        {stale.basis}
        {stale.capped
          ? " The sweep stopped at its cap, so this is a floor rather than the whole picture."
          : ""}
      </Typography>
    </Box>
  );
}

/**
 * What one agent will read at the top of its next run.
 *
 * ## The question the rest of this screen cannot answer
 *
 * Everything above lists what exists. An agent does not read what exists —
 * it reads at most five notes addressed to it and five written to everybody,
 * ranked, in an order that decides which one sits nearest the work. Somebody
 * who has just written an instruction wants to know it landed, and "it is in
 * the list" is not that answer.
 *
 * It is also the check that catches a broken recall on sight. The read that
 * feeds this was filtering one page of the collection rather than the
 * collection, so a note written today came back to nobody — a bug that had
 * been live for weeks behind a 200 and a short list, and that this panel
 * shows as an empty section the moment you open it.
 *
 * ## Quoted, not rebuilt
 *
 * `?recall=` returns the lines already assembled: the kernel picks which
 * half comes first, how many of each, and what a line says, and
 * `agents/runner.py` calls the same parameter. So this is the prompt rather
 * than a description of one. Rebuilding the assembly here would have been
 * the obvious way to write this component and the wrong one — a preview is
 * worth exactly its agreement with the thing it previews, and two
 * implementations agree until they do not.
 *
 * ## What it does not prove
 *
 * It is computed under **your** permissions, not the agent's. A memory
 * carries the tags of what it was drawn from, so the agent may read rows
 * this does not show — and nothing here can count them, because the kernel
 * returns what you may see and says nothing about the rest. That sentence
 * comes from the kernel with the answer rather than being written here, so
 * it cannot drift from what was actually done.
 */
function NextRun({ addressees, agent, preview, task, onTask, onAgent }) {
  if (!addressees.length) return null;
  return (
    <Box>
      <Typography variant="overline" sx={{ display: "block", color: "text.primary" }}>
        What one of them reads next
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "62ch", mb: 1.5 }}>
        Not what exists — what an agent is actually handed when it starts.
        The list above is the library; this is the page it opens on.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", alignItems: "flex-start" }}>
        <TextField
          select
          size="small"
          label="Look ahead for"
          value={agent}
          onChange={(e) => onAgent(e.target.value, task)}
          sx={{ minWidth: "16rem" }}
        >
          <MenuItem value="">Nobody</MenuItem>
          {addressees.map((a) => (
            <MenuItem key={a.app_type} value={a.app_type}>{a.label}</MenuItem>
          ))}
        </TextField>

        <Box component="form" sx={{ display: "flex", gap: 1 }}
             onSubmit={(e) => { e.preventDefault(); onAgent(agent, task); }}>
          <TextField
            size="small"
            label="If you asked it to…"
            value={task}
            onChange={(e) => onTask(e.target.value)}
            disabled={!agent}
            sx={{ minWidth: "20rem" }}
            helperText="What it reads depends on the job. Leave empty for the most recent instead."
          />
          <Button type="submit" size="small" disabled={!agent}>Show</Button>
        </Box>
      </Stack>

      {agent && !preview ? (
        <Loading label={`Reading what ${agent} will be told`} />
      ) : null}

      {preview ? (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {preview.to_this_agent} addressed to it, {preview.to_every_agent}{" "}
            to every agent, at most {preview.limit} of each.{" "}
            {preview.for
              ? `Ordered by nearness to \u201c${preview.for}\u201d.`
              : "Ordered by recency \u2014 give it a job above to see what it "
                + "would actually open on."}
          </Typography>

          {preview.lines.length === 0 ? (
            <Alert severity="warning" variant="outlined">
              <AlertTitle>It starts cold</AlertTitle>
              Nothing is addressed to {preview.agent} and nothing has been
              written to every agent, so this run begins knowing only what its
              prompt says. That is the state every run was in before any of
              this existed — it is not an error, but if you have written
              something for this agent it did not land.
            </Alert>
          ) : (
            // A pre, because it is a quotation. Rendering the lines as a
            // styled list would be this screen's idea of them rather than
            // the thing the model receives, and the whole value here is that
            // they are the same string.
            <Box
              component="pre"
              tabIndex={0}
              aria-label={`The notes ${preview.agent} will be given`}
              sx={{
                m: 0,
                p: 1.5,
                maxHeight: "22rem",
                overflow: "auto",
                borderRadius: 1,
                border: 1,
                borderColor: "divider",
                backgroundColor: "background.paper",
                fontFamily: "monospace",
                fontSize: "0.8125rem",
                lineHeight: 1.7,
                whiteSpace: "pre-wrap",
                overflowWrap: "anywhere",
                "&:focus-visible": {
                  outline: "2px solid",
                  outlineColor: "primary.main",
                  outlineOffset: 2,
                },
              }}
            >
              {[preview.preamble, "", ...preview.lines].join("\n")}
            </Box>
          )}

          {/* The qualification on the figure it qualifies, in the kernel's
              own words. */}
          <Typography variant="caption" color="text.secondary"
                      sx={{ display: "block", mt: 1, maxWidth: "62ch" }}>
            {preview.basis}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
}

/**
 * What the memories are about, rather than what they are filed under.
 *
 * ## Why this is a different question from the map above
 *
 * `subject` is the filing: an agent name. Two notes filed under different
 * agents can be about the same thing, and the map keyed on `subject` shows
 * them as unrelated because the field it is keyed on says they are. These
 * groups come from the text.
 *
 * ## Grouped by the kernel, not here
 *
 * Not a division of labour — a necessity. `api._public` strips the vector
 * from every response, so this page has nothing to cluster with, and serving
 * vectors so it could would put a second encoding of the content in the
 * payload for a page that only needs the answer.
 *
 * ## What the numbers beside a topic mean
 *
 * The count is members. **Cohesion is the weakest pair in the group**, not
 * the average, because topics are single-link connected components: A near B
 * and B near C makes one group even when A and C are unalike. An average
 * would hide the loose pair that joined them; the minimum is the pair a
 * reader needs to see. A group of one shows no cohesion at all rather than
 * 1.0, which would read as a strong result from a measurement never taken.
 *
 * ## The label is a quote
 *
 * The first words of the member most like all the others. Not a summary: a
 * generated label is a claim nobody checked sitting on top of a group whose
 * whole purpose is to be checkable, and quoting a real member means the
 * label is wrong only if the grouping is.
 */
function Topics({ topics, focus, onFocus }) {
  if (!topics || !topics.topics?.length) return null;
  const grouped = topics.topics.filter((t) => t.size > 1);
  const alone = topics.topics.filter((t) => t.size === 1);

  return (
    <Box>
      <Typography variant="overline" sx={{ display: "block", color: "text.primary" }}>
        What it is about
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "62ch", mb: 1.5 }}>
        Grouped by what the memories say rather than by who filed them, so two
        notes under different agents about the same thing land together. Choose
        one to narrow the list.
      </Typography>

      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
        {grouped.map((t) => (
          <TopicNode key={t.id} topic={t} focus={focus} onFocus={onFocus} />
        ))}
      </Stack>

      {grouped.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "62ch" }}>
          Nothing here is about the same thing as anything else yet, so every
          memory is its own topic. That is a fact about how few there are, not
          about how well they were grouped.
        </Typography>
      ) : null}

      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: "62ch", mt: 1.5 }}>
        {alone.length
          ? `${alone.length} ${alone.length === 1 ? "memory stands" : "memories stand"} alone. `
          : ""}
        {topics.unplaced?.length
          ? `${topics.unplaced.length} could not be grouped at all: written before this kernel embedded them, so there is nothing to compare. `
          : ""}
        Two memories share a topic when they are at least 70% alike — a
        threshold measured against this kernel rather than chosen, and one
        worth re-measuring as the number of memories grows.
      </Typography>
    </Box>
  );
}

/** One topic: a button, for the same reason a map node is one. */
function TopicNode({ topic, focus, onFocus }) {
  const active = focus?.kind === "topic" && focus.id === topic.id;
  return (
    <Box
      component="button"
      type="button"
      aria-pressed={active}
      onClick={() => onFocus(active ? null : {
        kind: "topic", id: topic.id, value: topic.label, members: topic.members,
      })}
      sx={{
        px: 1.25,
        py: 1,
        maxWidth: "26rem",
        textAlign: "left",
        font: "inherit",
        cursor: "pointer",
        borderRadius: 1,
        border: 1,
        borderColor: active ? "primary.main" : "divider",
        backgroundColor: "background.paper",
        color: "text.primary",
        "&:hover": { borderColor: "primary.main" },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: "baseline" }}>
        <Typography variant="body2" sx={{ fontWeight: active ? 600 : 500 }}>
          {topic.size}
        </Typography>
        <Typography variant="body2" sx={{ minWidth: 0 }}>
          {topic.label}
        </Typography>
      </Stack>
      {topic.cohesion !== null && topic.cohesion !== undefined ? (
        <Tooltip title="The weakest pair in this group, not the average. Topics chain: two memories can join a group through a third without being alike themselves, and an average would hide the loose pair that did it.">
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
            weakest pair {Math.round(topic.cohesion * 100)}% alike
          </Typography>
        </Tooltip>
      ) : null}
    </Box>
  );
}

/**
 * One row, badged with who is standing behind it.
 *
 * The badge is the point. A person's assertion is not evidence and an
 * agent's inference is not authority, and a reader who cannot tell them
 * apart cannot tell whether "this vendor pays on the 3rd" came from forty
 * payments or from something somebody typed once in March.
 */
function Entry({ row, busy, get, addressable, onReplace, onEdit, onRetire,
                onForget }) {
  const fields = row.fields || {};
  const byPerson = fields.asserted_by === "person";
  const cites = (fields.derived_from || []).length;
  // `size > 1` because the set holds the broadcast address before `/apps`
  // answers, and "nothing reads this" said over every row while a request is
  // in flight is a false claim in the direction that matters.
  const unread = addressable?.size > 1 && !addressable.has(fields.subject);
  const [evidence, setEvidence] = useState(null);
  const [checking, setChecking] = useState(false);

  // On demand, and one row at a time. The collection read does not carry
  // this: two hundred memories would be two hundred times however many
  // sources each names, and the answer is only interesting when somebody is
  // looking at a particular claim. A list that quietly did it would be a
  // list that got slow for a reason nothing on the page explained.
  async function check() {
    setChecking(true);
    try {
      const body = await get(`/memories/${row.id}`);
      setEvidence(body.evidence || null);
    } catch {
      setEvidence({ verdict: "unreadable",
                    basis: "This memory could not be re-read just now." });
    } finally {
      setChecking(false);
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" spacing={1}
             sx={{ alignItems: "baseline", flexWrap: "wrap", mb: 1 }}>
        <Tooltip title={byPerson
          ? "Somebody said so, and is accountable for it. It cites no sources, cannot be re-derived, and does not go stale on its own."
          : "An agent worked this out. It can be re-checked against what it was drawn from, and it can go stale."}>
          <Chip size="small"
                color={byPerson ? "primary" : "default"}
                variant={byPerson ? "filled" : "outlined"}
                label={byPerson ? "stated" : "derived"} />
        </Tooltip>
        <Tooltip title={unread
          ? "No agent asks for this address. A run recalls what names its app type, matched exactly — so this is stored and read by nothing. Usually the address is an agent's own name rather than the app it runs under."
          : "Who reads this. An agent recalls what is addressed to it and nothing else."}>
          <Typography variant="body2"
                      color={unread ? "warning.main" : "text.secondary"}
                      sx={{ flexGrow: 1 }}>
            {fields.subject
              ? `to ${addressLabel(fields.subject)}`
              : "addressed to nobody"}
            {/* In words as well as colour. A reader who cannot see the
                colour is exactly the reader this needs to reach. */}
            {unread ? " — read by nobody" : ""}
          </Typography>
        </Tooltip>
      </Stack>

      <Typography variant="body1" sx={{ mb: 1.5 }}>{fields.content}</Typography>

      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
        {byPerson ? (
          <Button size="small" disabled={busy} onClick={onEdit}>Edit</Button>
        ) : (
          <Tooltip title="An agent's memory is not edited in place — that would leave what it was drawn from naming records that no longer say what it says. Replacing it writes a new entry and leaves this one as evidence.">
            <span>
              <Button size="small" disabled={busy} onClick={onReplace}>
                Replace
              </Button>
            </span>
          </Tooltip>
        )}
        <Tooltip title="This stopped being true. The entry stays, readable by id, and so does the fact that somebody decided it was wrong.">
          <span>
            <Button size="small" color="inherit" disabled={busy}
                    onClick={onRetire}>
              Retire
            </Button>
          </span>
        </Tooltip>
        <Tooltip title="This should never have existed. The words go and do not come back; what stays is that it existed and who removed it. Needs a person \u2014 no schedule can do this.">
          <span>
            <Button size="small" color="error" disabled={busy}
                    onClick={onForget}>
              Delete
            </Button>
          </span>
        </Tooltip>
        {cites > 0 ? (
          <Button size="small" disabled={busy || checking} onClick={check}>
            {checking ? "Checking…" : "Check the evidence"}
          </Button>
        ) : null}
      </Stack>

      {evidence ? <Evidence evidence={evidence} /> : null}

      {fields.supersedes ? (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Replaces an earlier entry, which is still readable by id.
        </Typography>
      ) : null}
    </Paper>
  );
}

/**
 * What the sources say about themselves, and what that does not prove.
 *
 * The verdicts are deliberately not `verified` and `contradicted`. Deciding a
 * memory is *wrong* means re-running the derivation, which means the model;
 * what is free and exact is whether the **evidence moved**. Rendering "moved"
 * as "wrong" would overstate in exactly the way a tick saying "verified"
 * would, and the rule about a tick that overstates being worse than no tick
 * runs in both directions.
 *
 * So the basis is rendered with the verdict and not behind a disclosure. It
 * is the half that carries the claim.
 */
function Evidence({ evidence }) {
  const tone = {
    standing: "success",
    moved: "warning",
    gone: "warning",
    undated: "info",
    unknown: "info",
  }[evidence.verdict] || "info";

  const label = {
    standing: "The evidence has not moved",
    moved: "The evidence has moved since",
    gone: "The evidence is not there to check",
    undated: "Cannot tell whether the evidence moved",
  }[evidence.verdict] || "Nothing to check this against";

  return (
    <Alert severity={tone} variant="outlined" sx={{ mt: 1.5 }}>
      <AlertTitle>{label}</AlertTitle>
      <Typography variant="body2" sx={{ mb: (evidence.sources || []).length ? 1 : 0 }}>
        {evidence.basis}
      </Typography>
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
        {(evidence.sources || []).map((s) => (
          <Chip key={s.source} size="small" variant="outlined"
                label={`${s.source} · ${s.state}`} />
        ))}
      </Stack>
    </Alert>
  );
}

/**
 * The form, which says what it is about to do before it does it.
 *
 * Replacing an agent's memory is the case that needs saying out loud: the
 * person is not editing the row in front of them, they are writing a new one
 * that supersedes it. A dialog that looked like an edit and behaved like a
 * create would leave somebody holding a different id than the one they
 * thought they changed.
 */
function WriteDialog({ intent, permitted, addressees, busy, onClose, onWrite }) {
  const replacing = intent.mode === "replace";
  const editing = intent.mode === "edit";
  const existing = intent.row?.fields || {};

  const [content, setContent] = useState(
    replacing || editing ? existing.content || "" : "");
  // App types, never agent names. An existing row is re-addressed by nobody:
  // an entry addressed elsewhere is a different entry.
  const [to, setTo] = useState([]);
  const [tags, setTags] = useState([]);

  const addressing = !replacing && !editing;
  const everyone = to.includes(BROADCAST);
  const fixed = existing.subject || "";

  function submit() {
    const base = {
      content: content.trim(),
      asserted_by: "person",
      active: "true",
    };

    if (editing) {
      // An update, not a create. This dialog used to post `memory.create`
      // in every mode, so "Edit your entry" left the original standing and
      // added a second row saying something else — two live entries on the
      // same subject, with nothing marking either as the current one.
      onWrite([{
        path: "/actions/memory.update",
        body: { id: intent.row.id, fields: { content: base.content } },
        addressee: fixed,
      }]);
      return;
    }

    if (replacing) {
      const fields = { ...base, subject: fixed, supersedes: intent.row.id };
      onWrite([{
        path: "/actions/memory.create",
        body: tags.length ? { fields, tags } : { fields },
        addressee: fixed,
      }]);
      return;
    }

    onWrite(to.map((subject) => {
      const fields = { ...base, subject };
      return {
        path: "/actions/memory.create",
        body: tags.length ? { fields, tags } : { fields },
        addressee: subject === BROADCAST ? "every agent" : subject,
      };
    }));
  }

  return (
    // Composed, not configured. The kit `Dialog` is a focus-restoring
    // wrapper around MUI's and takes no `title` or `actions` — passing them
    // put two props into `...rest` that MUI ignored, so the dialog opened
    // with no heading and no buttons and nothing said why.
    <Dialog
      open
      onClose={busy ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="knowledge-write-title"
    >
      <DialogTitle id="knowledge-write-title">
        {replacing ? "Replace what an agent concluded"
          : editing ? "Edit your entry" : "Tell them something"}
      </DialogTitle>

      <DialogContent dividers>
      <Stack spacing={2} sx={{ pt: 1 }}>
        {replacing ? (
          <Alert severity="info" variant="outlined">
            <AlertTitle>This writes a new entry</AlertTitle>
            The agent's note is not changed. It stays as the evidence of what
            the agent concluded, and stops being the current answer because
            this entry names it. Editing it in place would leave what it was
            drawn from naming records that no longer say what it says.
          </Alert>
        ) : null}

        <TextField
          label="What do they need to know"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          multiline
          minRows={3}
          fullWidth
          helperText="An instruction, not a summary. This outranks anything an agent infers on the same subject."
        />

        {addressing ? (
          <>
            <TextField
              select
              // `slotProps.select`, not `SelectProps` — see the tags field
              // below for what the v5 spelling does here.
              slotProps={{
                select: {
                  multiple: true,
                  renderValue: (picked) => picked
                    .map((v) => (v === BROADCAST ? "Every agent" : v))
                    .join(", "),
                },
              }}
              label="Who are you telling"
              value={to}
              onChange={(e) => {
                const picked = typeof e.target.value === "string"
                  ? e.target.value.split(",") : e.target.value;
                // Every agent is not one more addressee, it is instead of
                // them: picking it alongside three named ones would write
                // four rows, three of which the fourth already covers.
                setTo(picked.includes(BROADCAST)
                  && !to.includes(BROADCAST)
                  ? [BROADCAST]
                  : picked.filter((v) => v !== BROADCAST || picked.length === 1));
              }}
              fullWidth
              helperText={
                everyone
                  ? "One entry, read by every agent — including ones added after today."
                  : to.length > 1
                    ? `${to.length} entries, one each, so each can be replaced or retired on its own.`
                    : "An agent reads what is addressed to it and nothing else."
              }
            >
              <MenuItem value={BROADCAST}>
                <Stack sx={{ minWidth: 0 }}>
                  <Typography variant="body2">Every agent</Typography>
                  <Typography variant="caption" color="text.secondary">
                    including agents added later
                  </Typography>
                </Stack>
              </MenuItem>
              {addressees.map((a) => (
                <MenuItem key={a.app_type} value={a.app_type}>
                  <Stack sx={{ minWidth: 0 }}>
                    <Typography variant="body2">{a.label}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {a.app_type}
                      {a.agents.length ? ` · runs ${a.agents.join(", ")}` : ""}
                    </Typography>
                  </Stack>
                </MenuItem>
              ))}
            </TextField>

            {/* The list is apps that declare an agent, and an app can run an
                agent under another name. Said here because the list shows
                both and a reader would otherwise have to guess which of the
                two the entry is filed under. */}
            {addressees.length === 0 ? (
              <Alert severity="warning" variant="outlined">
                <AlertTitle>There is nobody to tell</AlertTitle>
                No app in this organization declares an agent, so an entry
                written now would be read by nothing. It would still be
                stored, and still be here when one is added.
              </Alert>
            ) : null}
          </>
        ) : (
          <TextField
            label="Who this was told to"
            value={fixed || "nobody"}
            fullWidth
            disabled
            helperText={replacing
              ? "Carried from the entry being replaced, so both are addressed the same way."
              : "An entry addressed elsewhere is a different entry, so this cannot be changed here."}
          />
        )}

        {/* Only when the caller has tags to give. An empty select would read
            as a missing feature; the absence of tags on `Memory` is a
            decision (LO-121) and not an oversight. */}
        {permitted.length > 0 ? (
          <TextField
            select
            // `slotProps.select`, not `SelectProps`. The v5 spelling is
            // accepted, React does not warn, and the contents never reach
            // the element — so `multiple` would silently not apply and this
            // would be a single-select that looked like a multi.
            slotProps={{ select: { multiple: true } }}
            label="Who may see this"
            value={tags}
            onChange={(e) => setTags(
              typeof e.target.value === "string"
                ? e.target.value.split(",") : e.target.value)}
            fullWidth
            helperText="From what you can see yourself. You cannot write an entry more visible than you are."
          >
            {permitted.map((t) => (
              <MenuItem key={t} value={t}>{t}</MenuItem>
            ))}
          </TextField>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary">
              No tags. A memory carries none until they are computed from what
              it was derived from, and what is written here today holds no
              customer data at all.
            </Typography>
          </Box>
        )}
      </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit}
                disabled={busy || !content.trim()
                  || (addressing && to.length === 0)}>
          {replacing ? "Replace"
            : everyone ? "Tell every agent"
              : to.length > 1 ? `Tell ${to.length} agents`
                : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
